import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import Checkbox from '@mui/material/Checkbox';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { MouseEvent } from 'react';
import React from 'react';

import type { QMenuItemProps } from '../types';
import { Q_MENU_END_ICON_SX, Q_MENU_ICON_GLYPH_SIZE, Q_MENU_ICON_SLOT_SIZE, Q_MENU_ICON_SLOT_SX } from './iconSlot';
import { useQMenuClose } from './QMenuCloseContext';

/** Multi-select checkbox: no edge offset (conflicts with zero padding); icon sized to glyph. */
const MULTI_CHECKBOX_SX = {
  p: 0,
  '& .MuiSvgIcon-root': {
    fontSize: Q_MENU_ICON_GLYPH_SIZE,
  },
} as const;

/** Trailing interactive control (SegmentedTabs / Switch) — flexible width, not the icon slot. */
const Q_MENU_END_ACTION_SX = {
  ml: 1,
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
} as const;

export function QMenuItem(props: QMenuItemProps): React.ReactElement {
  const {
    label,
    subtext,
    startIcon,
    emptyStartIcon = false,
    endIcon,
    disabled = false,
    destructive = false,
    dense,
    id,
    autoFocus,
    tabIndex,
  } = props;

  const variant = props.variant ?? 'action';
  const endAction =
    (props.variant === undefined || props.variant === 'action') && 'endAction' in props ? props.endAction : undefined;
  const hasEndAction = Boolean(endAction);
  const hasSubtext = Boolean(subtext);
  const menuClose = useQMenuClose();

  const isToggleChecked = variant === 'toggle' && props.variant === 'toggle' && props.checked;
  const isOptionSelected = variant === 'option' && props.variant === 'option' && Boolean(props.selected);
  const isMultiOption = variant === 'option' && props.variant === 'option' && Boolean(props.multiple);
  /** Single-select leading checkmark: shown unless `showCheckmark` is explicitly false. */
  const showsCheckmark = props.variant === 'option' && !props.multiple && props.showCheckmark !== false;

  const color = destructive ? 'error.main' : undefined;
  const selectedColor = isToggleChecked || (isOptionSelected && !isMultiOption) ? 'primary.main' : color;

  const handleClick = (event: MouseEvent<HTMLElement>): void => {
    if (disabled) return;
    if (props.variant === 'toggle') {
      props.onCheckedChange(!props.checked);
      return;
    }
    // Control rows: label click should not dismiss; interaction lives in endAction.
    if (hasEndAction) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (props.onClick) {
      props.onClick(event);
    }
    // Multi-option stays open; endAction defaults to stay open; otherwise auto-dismiss
    // unless closeOnClick is false.
    const closeOnClick = props.closeOnClick ?? !hasEndAction;
    const shouldClose = !isMultiOption && closeOnClick && menuClose !== null;
    if (shouldClose) {
      menuClose.close();
    }
  };

  let leading: React.ReactNode = null;
  let role: string | undefined;
  let ariaChecked: boolean | undefined;

  if (props.variant === 'toggle') {
    role = 'menuitemcheckbox';
    ariaChecked = props.checked;
    leading = (
      <ListItemIcon sx={Q_MENU_ICON_SLOT_SX}>
        <CheckRoundedIcon
          sx={{
            color: props.checked ? 'primary.main' : 'transparent',
            opacity: props.checked ? 1 : 0,
          }}
        />
      </ListItemIcon>
    );
  } else if (props.variant === 'option' && props.multiple) {
    role = 'menuitemcheckbox';
    ariaChecked = Boolean(props.selected);
    leading = (
      <ListItemIcon sx={Q_MENU_ICON_SLOT_SX}>
        <Checkbox
          size='small'
          checked={Boolean(props.selected)}
          tabIndex={-1}
          disableRipple
          disabled={disabled}
          sx={MULTI_CHECKBOX_SX}
        />
      </ListItemIcon>
    );
  } else if (props.variant === 'option') {
    role = 'menuitemradio';
    ariaChecked = Boolean(props.selected);
    if (showsCheckmark) {
      leading = (
        <ListItemIcon sx={Q_MENU_ICON_SLOT_SX}>
          <CheckRoundedIcon
            sx={{
              color: props.selected ? 'primary.main' : 'transparent',
              opacity: props.selected ? 1 : 0,
            }}
          />
        </ListItemIcon>
      );
    } else if (emptyStartIcon && !startIcon) {
      // Align with sibling checkmark / icon rows when this option has no leading affordance.
      leading = <ListItemIcon sx={Q_MENU_ICON_SLOT_SX} />;
    }
  } else if (startIcon) {
    leading = (
      <ListItemIcon sx={{ ...Q_MENU_ICON_SLOT_SX, color: selectedColor ?? 'inherit' }}>{startIcon}</ListItemIcon>
    );
  } else if (emptyStartIcon) {
    // Reserve the same 24px slot toggles/checks use so mixed menus align labels.
    leading = <ListItemIcon sx={Q_MENU_ICON_SLOT_SX} />;
  }

  const iconTopAlignSx = hasSubtext
    ? {
        alignSelf: 'flex-start',
        mt: 0.25,
      }
    : undefined;

  return (
    <MenuItem
      id={id}
      dense={dense}
      disabled={disabled}
      disableRipple
      autoFocus={autoFocus}
      tabIndex={tabIndex}
      onClick={handleClick}
      // Only the checkmark-less single-select row needs the theme `action.selected` background;
      // a checkmark or checkbox already conveys selection, so the highlight would double up.
      selected={isOptionSelected && !isMultiOption && !showsCheckmark}
      role={role}
      aria-checked={ariaChecked}
      sx={{
        borderRadius: 'lg',
        px: (theme) => theme.spacing(1),
        py: (theme) => theme.spacing(dense ? 0.5 : 1),
        // Multi-line label+subtext: top-align icons (NestedQMenu pattern). Default center clips secondary.
        alignItems: hasSubtext ? 'flex-start' : 'center',
        color: selectedColor,
        whiteSpace: 'normal',
        minHeight: hasEndAction ? 32 : undefined,
        '& .MuiListItemIcon-root': {
          minWidth: Q_MENU_ICON_SLOT_SIZE,
          width: Q_MENU_ICON_SLOT_SIZE,
          justifyContent: 'center',
          color: selectedColor ?? 'inherit',
          ...iconTopAlignSx,
        },
      }}
    >
      {leading}
      {variant !== 'action' && startIcon ? (
        <ListItemIcon sx={{ ...Q_MENU_ICON_SLOT_SX, color: selectedColor ?? 'inherit', ...iconTopAlignSx }}>
          {startIcon}
        </ListItemIcon>
      ) : null}
      <Stack sx={{ flex: 1, minWidth: 0, py: hasSubtext ? 0 : undefined }}>
        <Typography
          variant='paragraphRegular'
          component='span'
          sx={{ fontWeight: 400, color: selectedColor ?? 'text.primary' }}
        >
          {label}
        </Typography>
        {subtext ? (
          <Typography
            variant='captionRegular'
            color='text.secondary'
            component='span'
            sx={{ display: 'block', mt: 0.25 }}
          >
            {subtext}
          </Typography>
        ) : null}
      </Stack>
      {endAction ? (
        <Stack
          component='span'
          sx={{
            ...Q_MENU_END_ACTION_SX,
            ...iconTopAlignSx,
          }}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          {endAction}
        </Stack>
      ) : endIcon ? (
        <Stack
          component='span'
          sx={{
            ...Q_MENU_END_ICON_SX,
            color: selectedColor ?? 'inherit',
            ...iconTopAlignSx,
          }}
        >
          {endIcon}
        </Stack>
      ) : null}
    </MenuItem>
  );
}
